// ユーザー棚卸しの純関数ロジック。従業員名簿CSV(SmartHR/カオナビ)と GW アクティブユーザーをメールで突合し、
// 「名簿に無いアクティブアカウント = 退職者アカウント残存の疑い」を検出する。CSV 解析は共有 ui/csv を再利用。
// GW 用の最小型(ReconcileUser{email,active})で持ち、SfUser 依存を避ける(ドメイン分離)。
import { parseCsv, detectColumns } from "../ui/csv";

export interface RosterRow {
  readonly email: string;
  readonly name: string;
  readonly status: string;
}

export interface RosterParseResult {
  readonly rows: readonly RosterRow[];
  readonly error?: string;
}

export function rosterFromCsv(text: string): RosterParseResult {
  const parsed = parseCsv(text);
  if (parsed.length < 2) {
    return { rows: [], error: "CSV にデータ行がありません。ヘッダ行 + 1行以上が必要です。" };
  }
  const header = parsed[0]!;
  const cols = detectColumns(header);
  if (cols.email < 0) {
    return { rows: [], error: "メールアドレス列が見つかりません（ヘッダに「メール」「mail」等の列が必要です）。" };
  }
  const rows = parsed
    .slice(1)
    .map((r) => ({
      email: (r[cols.email] ?? "").trim(),
      name: cols.name >= 0 ? (r[cols.name] ?? "").trim() : "",
      status: cols.status >= 0 ? (r[cols.status] ?? "").trim() : "",
    }))
    .filter((r) => r.email !== "");
  return { rows };
}

export interface ReconcileUser {
  readonly email: string;
  readonly active: boolean;
  // 退職者残存の判断材料として残存テーブルに表示する（最終ログインが古いほど残存の可能性が高い）。
  readonly lastLoginTime?: string;
}

export interface ReconcileResult {
  readonly residual: readonly ReconcileUser[]; // 名簿に無いアクティブユーザー(主検出)
  readonly notInDirectory: readonly RosterRow[]; // 名簿に居るが Directory に無い(参考)
  readonly matchedCount: number;
}

const norm = (email: string): string => email.trim().toLowerCase();

export function reconcileUsers(roster: readonly RosterRow[], users: readonly ReconcileUser[]): ReconcileResult {
  const rosterEmails = new Set(roster.map((r) => norm(r.email)));
  const active = users.filter((u) => u.active); // 既に無効化済みは対象外
  const residual = active.filter((u) => !rosterEmails.has(norm(u.email)));
  const userEmails = new Set(users.map((u) => norm(u.email)));
  const notInDirectory = roster.filter((r) => !userEmails.has(norm(r.email)));
  return { residual, notInDirectory, matchedCount: active.length - residual.length };
}
